import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { PlusCircle, Trash2, Search, RefreshCw } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

export default function DailyAttendanceListing() {
  const nav = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  // login context (same pattern you used)
  const finYearId = Number(localStorage.getItem("finYearId") || 0);
  const branchId = Number(localStorage.getItem("branchId") || 0);
  const branchType = String(localStorage.getItem("branchtype") || "HO"); // HO / BRANCH

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // dropdowns
  const [branches, setBranches] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);

  // filters (ASP fields)
  const [empName, setEmpName] = useState("");
  const [empCode, setEmpCode] = useState("");
  const [designation, setDesignation] = useState("Select");
  const [branch, setBranch] = useState("Select");
  const [department, setDepartment] = useState("Select");

  // UI search + paging
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // delete selection
  const [selected, setSelected] = useState(new Set());

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const dangerBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold text-white shadow-lg transition-all duration-200 ` +
    `bg-red-600 hover:bg-red-700 disabled:opacity-60`;

  // NOTE: dynamic tailwind bg-${baseColor}-${baseShade} may not compile in your build.
  // If you already configured safelist, keep it. Else replace with a fixed class.
  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl font-bold text-white shadow-lg transition-all duration-200 ` +
    `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`;

  const loadMeta = async () => {
    if (!finYearId) return toast.warn("Please select Financial Year.");
    if (!branchId) return toast.warn("branchId missing (login).");

    try {
      const res = await axios.get(`${BASE_URL}/api/payroll/daily-attendance/meta`, {
        params: { finYearId, branchId, branchType },
      });

      if (!res?.data?.success) throw new Error(res?.data?.message || "Meta failed");

      setBranches(res.data.branches || []);
      setDesignations(res.data.designations || []);
      setDepartments(res.data.departments || []);

      // default branch selection = login branch (like ASP)
      setBranch(String(branchId));
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load dropdowns");
    }
  };

  const loadList = async () => {
    if (!finYearId) return toast.warn("Please select Financial Year.");
    if (!branchId) return toast.warn("branchId missing (login).");

    try {
      setLoading(true);
      setSelected(new Set());

      const res = await axios.get(`${BASE_URL}/api/payroll/daily-attendance/list`, {
        params: {
          finYearId,
          branchId,
          branchType,
          empCode: empCode.trim(),
          empName: empName.trim(),
          designation: designation !== "Select" ? designation : "",
          branch: branch !== "Select" ? branch : "",
          department: department !== "Select" ? department : "%",
        },
      });

      if (!res?.data?.success) throw new Error(res?.data?.message || "List failed");
      setRows(res?.data?.rows || []);
      setPage(1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMeta().then(loadList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddNew = () => {
    localStorage.removeItem("DailyID");
    nav("/Payroll/DailyAttendanceEntry");
  };

const onView = (row) => {
  const empId = Number(row.fld_employeeid || row.fld_employeeId || 0);
  if (!empId) return toast.error("Employee ID missing");
  nav(`/Payroll/DailyAttendanceEntry/edit/${empId}`);
};



  const onViewAll = async () => {
    setEmpName("");
    setEmpCode("");
    setDesignation("Select");
    setDepartment("Select");
    // keep login branch selected like ASP
    setBranch(String(branchId));
    setSearch("");
    setPage(1);
    await loadList();
  };

  // client-side text search (extra like your request earlier)
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const b = (r.fld_BranchName ?? "").toString().toLowerCase();
      const name = (r.fld_Employee ?? "").toString().toLowerCase();
      const code = (r.fld_Employeecode ?? "").toString().toLowerCase();
      const type = (r.fld_typename ?? "").toString().toLowerCase();
      const desig = (r.fld_designationname ?? "").toString().toLowerCase();
      const date = (r.fld_date ?? r.dailydate ?? "").toString().toLowerCase();
      return (
        b.includes(q) ||
        name.includes(q) ||
        code.includes(q) ||
        type.includes(q) ||
        desig.includes(q) ||
        date.includes(q)
      );
    });
  }, [rows, search]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / pageSize)), [filtered.length, pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const toggleOne = (dailyId) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(dailyId)) n.delete(dailyId);
      else n.add(dailyId);
      return n;
    });
  };

  const toggleAllOnPage = (checked) => {
    setSelected((prev) => {
      const n = new Set(prev);
      for (const r of paged) {
        const id = Number(r.fld_DailyAttendance_ID || r.fld_employeeid || 0);
        if (!id) continue;
        if (checked) n.add(id);
        else n.delete(id);
      }
      return n;
    });
  };

  const onDelete = async () => {
    if (selected.size === 0) return toast.warn("Please select any one");
    const ok = window.confirm("Are you sure to delete?");
    if (!ok) return;

    try {
      setLoading(true);

      // send items with empcode + date so backend can replicate leaveOB clean logic safely
      const items = Array.from(selected).map((id) => {
        const row = rows.find((r) => Number(r.fld_DailyAttendance_ID || r.fld_employeeid) === Number(id));
        return {
          dailyId: id,
          employeeCode: row?.fld_Employeecode || row?.fld_employeecode || "",
          date: row?.fld_date || row?.dailydate || row?.fld_Date || "", // if SP returns date
        };
      });

      const res = await axios.post(`${BASE_URL}/api/payroll/daily-attendance/delete`, {
        finYearId,
        items,
      });

      if (res?.data?.success) {
        toast.success(res?.data?.message || "Deleted");
        await loadList();
      } else {
        toast.error(res?.data?.message || "Delete failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">DAILY ATTENDANCE LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top actions */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-3">
              <button className={primaryBtn} onClick={onAddNew}>
                <PlusCircle className="w-4 h-4" />
                Add New
              </button>

              <button className={ghostBtn} onClick={loadList} disabled={loading}>
                <Search className="w-4 h-4" />
                Search
              </button>

              <button className={ghostBtn} onClick={onViewAll} disabled={loading}>
                <RefreshCw className="w-4 h-4" />
                View All
              </button>

              <button className={dangerBtn} onClick={onDelete} disabled={loading}>
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 opacity-60" />
                <input
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className={inputBase + " pl-11 w-[340px]"}
                  placeholder="Search in table..."
                />
              </div>

              <select
                className={inputBase + " w-[150px]"}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>

          {/* Filters (wider like your request) */}
          <div className={cardBase + " p-5 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <div>
                <label className="text-sm opacity-80">Employee Name</label>
                <input value={empName} onChange={(e) => setEmpName(e.target.value)} className={inputBase} />
              </div>

              <div>
                <label className="text-sm opacity-80">Employee Code</label>
                <input value={empCode} onChange={(e) => setEmpCode(e.target.value)} className={inputBase} />
              </div>

              <div>
                <label className="text-sm opacity-80">Designation</label>
                <select value={designation} onChange={(e) => setDesignation(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {designations.map((d) => (
                    <option key={d.fld_desigid} value={d.fld_designationname}>
                      {d.fld_designationname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm opacity-80">Branch</label>
                <select value={branch} onChange={(e) => setBranch(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {/* your BindDropDownList likely returns {Text,Value} or {fld_branchname,fld_branchid} */}
                  {branches.map((b, idx) => {
                    const val = String(b.Value ?? b.fld_Branchid ?? b.fld_branchid1 ?? b.fld_branchId ?? b.fld_branchID ?? b.value ?? "");
                    const txt = String(b.Text ?? b.fld_BranchName ?? b.fld_branchName ?? b.text ?? val);
                    if (!val) return null;
                    return (
                      <option key={`${val}-${idx}`} value={val}>
                        {txt}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="text-sm opacity-80">Department</label>
                <select value={department} onChange={(e) => setDepartment(e.target.value)} className={inputBase}>
                  <option value="Select">Select</option>
                  {departments.map((d) => (
                    <option key={d.fld_deptid} value={String(d.fld_deptid)}>
                      {d.fld_departmentname}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-auto">
              {/* Increase width like you asked */}
              <table className="min-w-[1400px] w-full">
                <thead className={isDark ? "bg-white/10" : "bg-gray-50"}>
                  <tr className="text-sm">
                    <th className="px-4 py-3 text-center w-[80px]">S.No</th>
                    <th className="px-4 py-3 text-center w-[200px]">Branch</th>
                    <th className="px-4 py-3 text-center w-[280px]">Employee Name</th>
                    <th className="px-4 py-3 text-center w-[180px]">Employee Code</th>
                    <th className="px-4 py-3 text-center w-[180px]">Employee Type</th>
                    <th className="px-4 py-3 text-center w-[240px]">Designation</th>
                    {/* If your SP returns date, you can show it */}
                    {/* <th className="px-4 py-3 text-center w-[180px]">Date</th> */}

                    <th className="px-4 py-3 text-center w-[160px]">
                      <div className="flex items-center justify-center gap-2">
                        <input
                          type="checkbox"
                          onChange={(e) => toggleAllOnPage(e.target.checked)}
                          checked={paged.length > 0 && paged.every((r) => selected.has(Number(r.fld_DailyAttendance_ID || r.fld_employeeid)))}
                        />
                        Select
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : paged.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-10 text-center">
                        No Records Found!
                      </td>
                    </tr>
                  ) : (
                    paged.map((r, idx) => {
                      const dailyId = Number(r.fld_DailyAttendance_ID || r.fld_employeeid || 0);
                      const checked = selected.has(dailyId);

                      return (
                        <tr
                          key={`${dailyId}-${idx}`}
                          className={isDark ? "border-t border-white/10" : "border-t border-gray-200"}
                        >
                          <td className="px-4 py-3 text-center">{(page - 1) * pageSize + idx + 1}</td>
                          <td className="px-4 py-3 text-center">{r.fld_BranchName ?? "-"}</td>

                          {/* Employee name is link (ASP ButtonField View) */}
                         <td className="px-4 py-3 text-center">
  <button
    onClick={() => onView(r)}
    className={`font-bold underline ${
      isDark
        ? "text-blue-200 hover:text-blue-100"
        : "text-blue-700 hover:text-blue-900"
    }`}
  >
    {r.fld_employee ?? "-"}
  </button>
</td>

                          <td className="px-4 py-3 text-center">{r.fld_employeecode ?? "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_typename ?? "-"}</td>
                          <td className="px-4 py-3 text-center">{r.fld_designationname ?? "-"}</td>
                          {/* <td className="px-4 py-3 text-center">{r.fld_date ?? r.dailydate ?? "-"}</td> */}

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleOne(dailyId)}
                              disabled={!dailyId}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={`flex flex-wrap items-center justify-between gap-3 p-4 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
              <div className="text-sm opacity-80">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, filtered.length)} of {filtered.length}
              </div>

              <div className="flex items-center gap-2">
                <button className={ghostBtn} onClick={() => setPage(1)} disabled={page === 1}>
                  {"<<"}
                </button>
                <button className={ghostBtn} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  {"<"}
                </button>

                <div className="px-4 py-2 rounded-2xl text-sm font-bold border border-gray-200 dark:border-white/10">
                  Page {page} / {totalPages}
                </div>

                <button className={ghostBtn} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  {">"}
                </button>
                <button className={ghostBtn} onClick={() => setPage(totalPages)} disabled={page === totalPages}>
                  {">>"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
}
